import { FC, useContext, useEffect, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import { toast } from 'react-toastify';
import { CREATE_NOTES, GET_NOTES } from '../../Pages/company/gql';
import { candidateNotesCategories } from '../../utils/constant';
import { customSelectDropDown } from '../styled/customSelect';
import { formatDateUS, createMarkup } from '../../utils/commonFn';
import { AppContext } from '../../context';

interface Props {
  candidateId: string;
  /** When the recruiter arrives here via a Called/Emailed status pick. */
  contextStatus?: string | null;
  /** Category value to pre-select (e.g. "Phone Call" / "Email Sent"). */
  prefillCategory?: string | null;
  onClearContext?: () => void;
}

// Strip the Quill HTML wrapper to decide whether the note actually has content.
const isQuillEmpty = (value: string) =>
  !value || value.replace(/<(.|\n)*?>/g, '').trim().length === 0;

const NotesTab: FC<Props> = ({
  candidateId,
  contextStatus,
  prefillCategory,
  onClearContext,
}) => {
  const { theme } = useContext(AppContext);
  const [category, setCategory] = useState<any>(null);
  const [content, setContent] = useState<string>('');

  const [createNote, { loading: saving }] = useMutation(CREATE_NOTES);
  const [fetchNotes, { data, loading: loadingNotes }] = useLazyQuery(GET_NOTES, {
    fetchPolicy: 'network-only',
  });

  const loadNotes = () => {
    if (!candidateId) return;
    fetchNotes({
      variables: {
        id: candidateId,
        notePayload: { type: 'Candidate', compensationNote: false },
        page: 1,
        limit: 20,
      },
    });
  };

  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  // Pre-select the category when arriving from a status pick.
  useEffect(() => {
    if (prefillCategory) {
      const match = candidateNotesCategories.find(
        (c: any) => c.value === prefillCategory
      );
      if (match) setCategory(match);
    }
  }, [prefillCategory]);

  const notes: any[] = data?.getNotes?.notes ?? [];
  const canSave = !!candidateId && !!category && !isQuillEmpty(content) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      const res = await createNote({
        variables: {
          content,
          noteableId: candidateId,
          noteableType: 'Candidate',
          category: category.value,
          isCompenstaionNote: false,
        },
      });
      if (res?.errors) {
        toast.error(res.errors[0]?.message || 'Failed to save note');
        return;
      }
      toast.success('Note saved');
      setContent('');
      onClearContext?.();
      loadNotes();
    } catch {
      toast.error('Failed to save note');
    }
  };

  return (
    <div className="workspace-notes">
      {contextStatus && (
        <div className="workspace-notes__context">
          Status set to <strong>{contextStatus}</strong>.
        </div>
      )}

      <strong className="text-14">Add Note</strong>

      <div className="workspace-notes__field">
        <label className="workspace-notes__label">
          Category <span className="text-danger">*</span>
        </label>
        <Select
          value={category}
          onChange={(opt: any) => setCategory(opt)}
          options={candidateNotesCategories}
          placeholder="Select category"
          classNamePrefix="select"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          styles={{
            ...customSelectDropDown(theme),
            menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
          }}
        />
      </div>

      <div className="workspace-notes__field">
        <label className="workspace-notes__label">Note</label>
        <div className="workspace-notes__editor">
          <ReactQuill value={content} onChange={setContent} placeholder="Enter note…" />
        </div>
      </div>

      <button
        type="button"
        className="btn btn-success btn-block workspace-notes__save"
        disabled={!canSave}
        onClick={handleSave}
      >
        {saving ? 'Saving…' : 'Save Note'}
      </button>

      <hr className="workspace-notes__divider" />

      <strong className="text-14">Recent Notes{notes.length ? ` · ${notes.length}` : ''}</strong>

      {loadingNotes && notes.length === 0 ? (
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>Loading notes…</p>
      ) : notes.length === 0 ? (
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>No notes yet.</p>
      ) : (
        <div className="workspace-notes__list">
          {notes.map((note) => (
            <div key={note.id} className="workspace-notes__item">
              <div className="workspace-notes__item-meta">
                {note.category && (
                  <span className="badge badge-info workspace-notes__cat">
                    {note.category}
                  </span>
                )}
                <span className="text-muted">
                  {note.user?.fullName || 'Unknown'}
                  {note.createdAt ? ` · ${formatDateUS(note.createdAt)}` : ''}
                </span>
              </div>
              <div
                className="workspace-notes__item-body"
                dangerouslySetInnerHTML={createMarkup(note.content || '')}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotesTab;
